import { CanvaBrandLink } from 'domains/accountDomain';
import { useEffect, useState } from 'react';
import accountService from 'services/accountService';

interface CanvaBrandRecognition extends CanvaBrandLink {
  loading: boolean;
  /**
   * The buyer came from Canva with a brand that is mapped to a RevShop org, so the order really did
   * run on their own organization's settings.
   */
  isLinkedCanvaBrand: boolean;
  /**
   * The buyer came from Canva with a brand that has no RevShop org mapping, so their order was
   * placed against the Canva parent org rather than their own organization's settings.
   */
  isUnlinkedCanvaBrand: boolean;
}

/**
 * Recognition state for the signed-in user's Canva brand. Both flags stay false unless the lookup
 * confirms a brand — a plain RevShop buyer, a failed lookup, or the pending first render says
 * nothing about Canva, so neither the recognized note nor the team cross-sell shows on a guess.
 */
export const useCanvaBrandRecognition = (): CanvaBrandRecognition => {
  const [link, setLink] = useState<CanvaBrandLink & { loading: boolean }>({
    loading: true,
    linked: true,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await accountService.getCanvaBrandLink();
        if (!cancelled) {
          setLink({
            loading: false,
            canvaBrandId: result?.canvaBrandId,
            linked: result?.linked !== false,
          });
        }
      } catch {
        if (!cancelled) {
          setLink({ loading: false, linked: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    ...link,
    isLinkedCanvaBrand: Boolean(link.canvaBrandId) && link.linked,
    isUnlinkedCanvaBrand: Boolean(link.canvaBrandId) && !link.linked,
  };
};
